import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AudioLines, Menu, X } from "lucide-react";
import { SigninLink } from "../common/SigninLink";

// Shared landing primitives extracted from FeaturesProof.jsx.
// Byte-preserved JSX to guarantee visual parity between
// /design-proof/features (frozen visual reference) and the
// production V2 landing composed via LandingV2.jsx.

// Language toggle: reuses the exact DashboardLayout.jsx segmented
// pattern (lines 420-456), themed for the dark navy Features header.
export const LangToggle = ({ lang, setLang }) => (
  <div
    data-testid="lang-toggle"
    role="group"
    aria-label="Language"
    className="inline-flex items-center rounded-md border border-white/25 p-0.5 bg-white/5"
  >
    <button
      type="button"
      onClick={() => setLang("en")}
      aria-pressed={lang === "en"}
      data-testid="lang-toggle-en"
      className={
        "px-2.5 py-1 text-xs font-semibold rounded-[5px] transition " +
        (lang === "en" ? "bg-white text-[var(--jc-navy)]" : "text-white/70 hover:text-white")
      }
    >
      English
    </button>
    <button
      type="button"
      onClick={() => setLang("es")}
      aria-pressed={lang === "es"}
      data-testid="lang-toggle-es"
      className={
        "px-2.5 py-1 text-xs font-semibold rounded-[5px] transition " +
        (lang === "es" ? "bg-white text-[var(--jc-navy)]" : "text-white/70 hover:text-white")
      }
    >
      Español
    </button>
  </div>
);

export const Header = ({ c, lang, setLang, activePage = "features" }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hamburgerRef = useRef(null);
  const closeBtnRef = useRef(null);
  const menuLabelOpen = lang === "es" ? "Abrir menú" : "Open menu";
  const menuLabelClose = lang === "es" ? "Cerrar menú" : "Close menu";

  // Close on Esc; lock body scroll while open; restore focus to
  // hamburger on close. Standard accessible drawer minimum.
  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setIsMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move focus into the drawer for keyboard users.
    setTimeout(() => { try { closeBtnRef.current?.focus(); } catch { /* noop */ } }, 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      try { hamburgerRef.current?.focus(); } catch { /* noop */ }
    };
  }, [isMenuOpen]);

  const closeMenu = () => setIsMenuOpen(false);

  return (
  <header className="bg-[var(--jc-navy)] text-white" data-testid="proof-header">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
      <Link
        to="/"
        aria-label="JobClose AI"
        className="inline-flex items-center gap-3 min-w-0"
        data-testid="proof-logo"
      >
        <img
          src="/brand/jobclose-ai-approved-logo-mark.png"
          alt=""
          style={{ height: 44, width: "auto" }}
          className="shrink-0 select-none"
          draggable={false}
        />
        <div className="leading-tight">
          <div className="font-display text-[22px] tracking-tight text-white whitespace-nowrap">
            JobClose <span className="text-[var(--jc-blue)]">AI</span>
          </div>
          <div className="text-white/60 text-[11px] font-medium">{c.tagline}</div>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-[14px]">
        {activePage === "how" ? (
          <Link to="/how-it-works" className="text-white font-semibold relative" data-testid="nav-how">
            {c.nav.how}
            <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[var(--jc-blue)] rounded-full" />
          </Link>
        ) : (
          <Link to="/how-it-works" className="text-white/75 hover:text-white transition-colors" data-testid="nav-how">
            {c.nav.how}
          </Link>
        )}
        {activePage === "features" ? (
          <Link to="/features" className="text-white font-semibold relative" data-testid="nav-features">
            {c.nav.features}
            <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[var(--jc-blue)] rounded-full" />
          </Link>
        ) : (
          <Link to="/features" className="text-white/75 hover:text-white transition-colors" data-testid="nav-features">
            {c.nav.features}
          </Link>
        )}
        {activePage === "pricing" ? (
          <Link to="/pricing" className="text-white font-semibold relative" data-testid="nav-pricing">
            {c.nav.pricing}
            <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[var(--jc-blue)] rounded-full" />
          </Link>
        ) : (
          <Link to="/pricing" className="text-white/75 hover:text-white transition-colors" data-testid="nav-pricing">
            {c.nav.pricing}
          </Link>
        )}
        {activePage === "story" ? (
          <Link to="/our-story" className="text-white font-semibold relative" data-testid="nav-story">
            {c.nav.story}
            <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[var(--jc-blue)] rounded-full" />
          </Link>
        ) : (
          <Link to="/our-story" className="text-white/75 hover:text-white transition-colors" data-testid="nav-story">
            {c.nav.story}
          </Link>
        )}
      </nav>
      <div className="hidden md:flex items-center gap-4 text-[14px]">
        <LangToggle lang={lang} setLang={setLang} />
        <SigninLink className="text-white/85 hover:text-white transition-colors" data-testid="proof-header-signin">
          {c.nav.signin}
        </SigninLink>
        <Link
          to="/schedule-a-demo"
          className={
            "jc-btn-blue px-4 py-2 rounded-md flex items-center gap-2 font-semibold " +
            (activePage === "demo" ? "ring-2 ring-white/40" : "")
          }
          data-testid="proof-header-cta"
        >
          {c.nav.demo} <ArrowRight size={14} />
        </Link>
      </div>
      {/* Mobile lang toggle + hamburger (PO 2026-02 · mobile public
          nav correction). Desktop nav above remains unchanged. */}
      <div className="md:hidden flex items-center gap-2">
        <LangToggle lang={lang} setLang={setLang} />
        <button
          ref={hamburgerRef}
          type="button"
          onClick={() => setIsMenuOpen(true)}
          aria-label={menuLabelOpen}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-public-nav"
          data-testid="mobile-nav-hamburger"
          className="inline-flex items-center justify-center rounded-md border border-white/25 bg-white/5 hover:bg-white/10 transition-colors"
          style={{ width: 44, height: 44 }}
        >
          <Menu size={22} aria-hidden="true" />
        </button>
      </div>
    </div>

    {/* Mobile drawer — bounded scope per PO audit. Below md only. */}
    {isMenuOpen && (
      <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-nav-drawer-root">
        <button
          type="button"
          aria-label={menuLabelClose}
          onClick={closeMenu}
          className="absolute inset-0 bg-black/60"
          data-testid="mobile-nav-backdrop"
        />
        <div
          id="mobile-public-nav"
          role="dialog"
          aria-modal="true"
          aria-label={c.nav.how + " / " + c.nav.features + " / " + c.nav.pricing + " / " + c.nav.story}
          data-testid="mobile-nav-drawer"
          className="absolute top-0 right-0 h-full w-[86%] max-w-[360px] bg-[var(--jc-navy)] text-white shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between px-5 h-20 border-b border-white/10">
            <div className="font-display text-[18px] tracking-tight">
              JobClose <span className="text-[var(--jc-blue)]">AI</span>
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={closeMenu}
              aria-label={menuLabelClose}
              data-testid="mobile-nav-close"
              className="inline-flex items-center justify-center rounded-md border border-white/25 bg-white/5 hover:bg-white/10 transition-colors"
              style={{ width: 44, height: 44 }}
            >
              <X size={22} aria-hidden="true" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-5 py-6 flex flex-col gap-1 text-[16px]">
            <Link
              to="/how-it-works"
              onClick={closeMenu}
              className="py-3 px-2 rounded-md text-white/90 hover:bg-white/5 hover:text-white transition-colors font-semibold min-h-[44px] flex items-center"
              data-testid="mobile-nav-how"
            >
              {c.nav.how}
            </Link>
            <Link
              to="/features"
              onClick={closeMenu}
              className="py-3 px-2 rounded-md text-white/90 hover:bg-white/5 hover:text-white transition-colors font-semibold min-h-[44px] flex items-center"
              data-testid="mobile-nav-features"
            >
              {c.nav.features}
            </Link>
            <Link
              to="/pricing"
              onClick={closeMenu}
              className="py-3 px-2 rounded-md text-white/90 hover:bg-white/5 hover:text-white transition-colors font-semibold min-h-[44px] flex items-center"
              data-testid="mobile-nav-pricing"
            >
              {c.nav.pricing}
            </Link>
            <Link
              to="/our-story"
              onClick={closeMenu}
              className="py-3 px-2 rounded-md text-white/90 hover:bg-white/5 hover:text-white transition-colors font-semibold min-h-[44px] flex items-center"
              data-testid="mobile-nav-story"
            >
              {c.nav.story}
            </Link>
          </nav>
          <div className="px-5 pb-6 pt-2 border-t border-white/10 flex flex-col gap-3">
            {/* PO 2026-02 · mobile hamburger Sign In. Existing
                contractors need a return-to-app path on iPhone; desktop
                already renders `nav.signin` at line ~139. Uses the
                same react-router destination (`/login`) as desktop so
                same-origin routing behavior is preserved. Rendered as
                an outline secondary CTA above the primary Demo CTA so
                the demo hierarchy stays intact. */}
            <SigninLink
              onClick={closeMenu}
              data-testid="mobile-nav-signin"
              className="w-full px-4 py-3 rounded-md flex items-center justify-center gap-2 font-semibold min-h-[44px] border border-white/25 bg-white/5 hover:bg-white/10 text-white transition-colors"
            >
              {c.nav.signin}
            </SigninLink>
            <Link
              to="/schedule-a-demo"
              onClick={closeMenu}
              data-testid="mobile-nav-demo"
              className="jc-btn-blue w-full px-4 py-3 rounded-md flex items-center justify-center gap-2 font-semibold min-h-[44px]"
            >
              {c.nav.demo} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    )}
  </header>
  );
};

export const InitialsAvatar = ({ initials, size = 40, variant = "navy", ring = false, testid }) => {
  const styles = variant === "blue"
    ? { background: "var(--jc-blue)", color: "#fff" }
    : { background: "var(--jc-navy)", color: "#fff" };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center justify-center rounded-full font-semibold ${ring ? "ring-2 ring-white" : ""}`}
      style={{
        ...styles, width: size, height: size,
        fontSize: Math.max(11, Math.round(size * 0.36)),
        letterSpacing: "0.02em",
        fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif",
        flexShrink: 0,
      }}
    >
      {initials}
    </span>
  );
};

export const VoiceBubble = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="relative rounded-2xl bg-[#0B1E3E] text-white shadow-lg px-4 py-3 flex items-start gap-3 border border-white/10"
  >
    <span className="flex-shrink-0 w-6 h-6 rounded-md bg-[var(--jc-blue)]/20 grid place-items-center text-[var(--jc-blue)]">
      <AudioLines size={14} strokeWidth={2.4} />
    </span>
    <span className="text-[13.5px] leading-snug">{children}</span>
  </div>
);

export const IconTile = ({ icon: Icon, size = 40, variant = "solid" }) => {
  const solid = variant === "solid";
  return (
    <span
      className={`flex-shrink-0 grid place-items-center rounded-xl ${
        solid ? "bg-[var(--jc-blue)] text-white"
              : "bg-[#EEF3FE] text-[var(--jc-blue)] border border-[#DBE6FB]"}`}
      style={{ width: size, height: size }}
    >
      <Icon size={size >= 40 ? 20 : 16} strokeWidth={2} />
    </span>
  );
};

/* Environmental Scene — new integration model.
   The section background carries the visual temperature; the scene
   photograph sits in a soft radial-gradient "atmospheric pool" behind
   it that continues the scene's dominant hue OUT into the section.
   The image itself is rendered natural-edge without CSS masks —
   avoiding the white halo. Only where a scene edge sits DIRECTLY
   next to copy do we apply a very short (5-6%) fade toward the
   section background, so the transition happens between two
   COMPATIBLE tones rather than image→pure-white. */
export const EnvScene = ({
  src,
  alt,
  ratio = "4/3",
  className = "",
  testid,
  ambient = "warm", // 'warm' | 'cool' | 'neutral'
  fadeSides = [], // subtle short fade against copy-adjacent edges only
}) => {
  const ambientBg =
    ambient === "cool"
      ? "radial-gradient(ellipse 70% 60% at 50% 50%, #EAF0F5 0%, transparent 70%)"
      : ambient === "neutral"
      ? "radial-gradient(ellipse 70% 60% at 50% 50%, #F3EFE9 0%, transparent 70%)"
      : "radial-gradient(ellipse 70% 60% at 50% 55%, #F4EBD8 0%, transparent 70%)";

  const stops = fadeSides.length
    ? fadeSides
        .map((side) => {
          const dir =
            side === "left" ? "to right"
            : side === "right" ? "to left"
            : side === "top" ? "to bottom"
            : "to top";
          return `linear-gradient(${dir}, transparent 0, #000 5%)`;
        })
        .join(", ")
    : null;
  const maskStyle = stops
    ? { WebkitMaskImage: stops, maskImage: stops }
    : {};

  return (
    <div
      data-testid={testid}
      className={`relative ${className}`}
      style={{ aspectRatio: ratio }}
    >
      {/* Atmospheric pool behind the photograph */}
      <div
        aria-hidden
        className="absolute inset-0 -m-6 rounded-[2rem]"
        style={{ background: ambientBg }}
      />
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover select-none rounded-[1.25rem]"
        style={maskStyle}
      />
    </div>
  );
};

export const ValueStrip = ({ items, testid }) => (
  <div
    data-testid={testid}
    className="mt-12 rounded-2xl bg-white/70 backdrop-blur border border-white shadow-sm"
  >
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-6 p-6">
      {items.map((it) => (
        <div key={it.t} className="flex items-start gap-3">
          <IconTile icon={it.icon} size={36} variant="light" />
          <div>
            <div className="text-[14px] font-semibold text-[var(--jc-text)] leading-tight">{it.t}</div>
            <div className="text-[12.5px] text-[var(--jc-text-soft)] mt-0.5 leading-snug">{it.b}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Section-level environmental wrapper. Each section is given a
// distinct, restrained warm/cool neutral base so the photograph has
// a natural visual environment to transition into. Kept intentionally
// subtle — the page still reads as one cohesive light JobClose AI
// experience, not six colored blocks.
export const EnvSection = ({ tone, id, testid, children }) => {
  // Section base backgrounds — barely-perceptible cream (warm) or
  // barely-perceptible cool. These replace the previous flat white,
  // which caused CSS mask fades to bloom into visible haze.
  const base = {
    warmSoft:   "linear-gradient(180deg, #FAF6EE 0%, #F6EFDF 100%)",
    warmMid:    "linear-gradient(180deg, #F7F1E4 0%, #F0E7D2 100%)",
    warmDeep:   "linear-gradient(180deg, #F3ECDB 0%, #ECE1C7 100%)",
    coolSoft:   "linear-gradient(180deg, #F3F6FA 0%, #E9EEF5 100%)",
    heroWarm:   "radial-gradient(ellipse 90% 80% at 55% 45%, #FCF6E8 0%, #FAF3E0 60%, #F7EDD3 100%)",
  }[tone] || "#FFFFFF";
  return (
    <section
      id={id}
      data-testid={testid}
      style={{ background: base }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        {children}
      </div>
    </section>
  );
};
